import React from 'react';
import { View, Text, ActivityIndicator } from 'react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';

export type AccountActionVariant = 'destructive' | 'primary' | 'secondary';

/**
 * Full-width action for the account-deletion screens.
 *
 * Sized per <pressable_sizing>: the outer View owns height, radius and colour;
 * the pressable fills it with `flex: 1` and repeats `minHeight` (as ModalAction
 * does) so no wrapper between the two can collapse it to zero height.
 */
export function AccountActionButton({
  label,
  onPress,
  variant = 'primary',
  disabled = false,
  loading = false,
  icon,
  accessibilityHint,
}: {
  label: string;
  onPress: () => void;
  variant?: AccountActionVariant;
  disabled?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
  accessibilityHint?: string;
}) {
  const colors = useThemeColors();
  const palette: Record<AccountActionVariant, { bg: string; fg: string; border: string }> = {
    destructive: { bg: colors.error, fg: colors.text.inverse, border: colors.error },
    primary: { bg: colors.primary[600], fg: colors.text.inverse, border: colors.primary[600] },
    secondary: { bg: colors.surface.primary, fg: colors.text.primary, border: colors.border.medium },
  };
  const { bg, fg, border } = palette[variant];
  const inert = disabled || loading;

  return (
    <View
      style={{
        minHeight: 52,
        borderRadius: 14,
        backgroundColor: bg,
        borderWidth: 1,
        borderColor: border,
        overflow: 'hidden',
        // On web AnimatedPressable dims disabled itself; dimming here too would compound.
        opacity: disabled && !isWeb ? 0.45 : 1,
      }}
    >
      <AnimatedPressable
        onPress={onPress}
        disabled={inert}
        accessibilityRole="button"
        accessibilityLabel={label}
        accessibilityHint={accessibilityHint}
        accessibilityState={{ disabled: inert, busy: loading }}
        className="flex-row items-center justify-center gap-2 px-5"
        style={{ flex: 1, minHeight: 52 }}
      >
        {loading ? <ActivityIndicator size="small" color={fg} /> : icon}
        <Text numberOfLines={1} style={{ color: fg, fontSize: 16, fontWeight: '600' }}>
          {label}
        </Text>
      </AnimatedPressable>
    </View>
  );
}
